/**
 * ContentGuideSection — localized long-form service/country/industry guide.
 * Uses language-aware datasets to avoid English leakage on non-EN routes.
 */
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { ArrowRight, BookOpen, CheckCircle2 } from 'lucide-react';
import { getBPOPageTranslations } from '@/lib/bpo-page-i18n';
import { getDigitalPageTranslations } from '@/lib/digital-page-i18n';
import { getBPODetailContent } from '@/lib/bpo-detail-content-i18n';
import { getBPOFallbackCopy, getDeepDiveLabels } from '@/lib/service-deepdive-i18n';
import { UI } from '@/lib/ui-translations';
import type { SupportedLanguage } from '@/lib/i18n';

interface ContentGuideSectionProps {
  entityName: string;
  entityType: 'country' | 'industry' | 'service';
  keywords?: string[];
}

const GUIDE_COPY: Record<string, {
  completeGuide: string;
  overview: string;
  capabilities: string;
  outcomes: string;
  deployment: string;
  faq: string;
  tailoredPlan: string;
}> = {
  en: { completeGuide: 'Complete Guide', overview: 'Overview', capabilities: 'What Is Included', outcomes: 'Expected Outcomes', deployment: 'Deployment Plan', faq: 'Frequently Asked Questions', tailoredPlan: 'Need a tailored plan for' },
  fr: { completeGuide: 'Guide Complet', overview: 'Vue d’ensemble', capabilities: 'Ce qui est inclus', outcomes: 'Résultats attendus', deployment: 'Plan de déploiement', faq: 'Questions fréquentes', tailoredPlan: 'Besoin d’un plan sur mesure pour' },
  es: { completeGuide: 'Guía Completa', overview: 'Resumen', capabilities: 'Qué incluye', outcomes: 'Resultados esperados', deployment: 'Plan de despliegue', faq: 'Preguntas frecuentes', tailoredPlan: '¿Necesita un plan a medida para' },
  ar: { completeGuide: 'الدليل الكامل', overview: 'نظرة عامة', capabilities: 'ما هو متضمن', outcomes: 'النتائج المتوقعة', deployment: 'خطة الإطلاق', faq: 'الأسئلة الشائعة', tailoredPlan: 'هل تحتاج خطة مخصصة لـ' },
  de: { completeGuide: 'Kompletter Leitfaden', overview: 'Überblick', capabilities: 'Leistungsumfang', outcomes: 'Erwartete Ergebnisse', deployment: 'Rollout-Plan', faq: 'Häufige Fragen', tailoredPlan: 'Benötigen Sie einen individuellen Plan für' },
  it: { completeGuide: 'Guida Completa', overview: 'Panoramica', capabilities: 'Cosa è incluso', outcomes: 'Risultati attesi', deployment: 'Piano di avvio', faq: 'Domande frequenti', tailoredPlan: 'Serve un piano su misura per' },
  pt: { completeGuide: 'Guia Completo', overview: 'Visão geral', capabilities: 'O que está incluído', outcomes: 'Resultados esperados', deployment: 'Plano de implantação', faq: 'Perguntas frequentes', tailoredPlan: 'Precisa de um plano sob medida para' },
  nl: { completeGuide: 'Complete Gids', overview: 'Overzicht', capabilities: 'Wat is inbegrepen', outcomes: 'Verwachte resultaten', deployment: 'Uitrolplan', faq: 'Veelgestelde vragen', tailoredPlan: 'Heeft u een maatwerkplan nodig voor' },
  pl: { completeGuide: 'Kompletny Przewodnik', overview: 'Przegląd', capabilities: 'Zakres usługi', outcomes: 'Oczekiwane wyniki', deployment: 'Plan wdrożenia', faq: 'Najczęstsze pytania', tailoredPlan: 'Potrzebujesz planu szytego na miarę dla' },
  ru: { completeGuide: 'Полное Руководство', overview: 'Обзор', capabilities: 'Что включено', outcomes: 'Ожидаемые результаты', deployment: 'План запуска', faq: 'Частые вопросы', tailoredPlan: 'Нужен индивидуальный план для' },
  zh: { completeGuide: '完整指南', overview: '概览', capabilities: '包含内容', outcomes: '预期结果', deployment: '部署计划', faq: '常见问题', tailoredPlan: '需要为以下对象定制方案：' },
  ja: { completeGuide: '完全ガイド', overview: '概要', capabilities: '含まれる内容', outcomes: '期待される成果', deployment: '導入計画', faq: 'よくある質問', tailoredPlan: '次に向けた最適プランが必要ですか：' },
  tr: { completeGuide: 'Kapsamlı Rehber', overview: 'Genel bakış', capabilities: 'Neler dahil', outcomes: 'Beklenen sonuçlar', deployment: 'Devreye alma planı', faq: 'Sık sorular', tailoredPlan: 'Şunun için özel bir plana mı ihtiyacınız var' },
  ko: { completeGuide: '완전 가이드', overview: '개요', capabilities: '포함 범위', outcomes: '예상 성과', deployment: '도입 계획', faq: '자주 묻는 질문', tailoredPlan: '다음 대상에 맞춘 맞춤 계획이 필요하신가요:' },
  sv: { completeGuide: 'Komplett Guide', overview: 'Översikt', capabilities: 'Det som ingår', outcomes: 'Förväntade resultat', deployment: 'Lanseringsplan', faq: 'Vanliga frågor', tailoredPlan: 'Behöver du en skräddarsydd plan för' },
  da: { completeGuide: 'Komplet Guide', overview: 'Overblik', capabilities: 'Hvad der er inkluderet', outcomes: 'Forventede resultater', deployment: 'Implementeringsplan', faq: 'Ofte stillede spørgsmål', tailoredPlan: 'Har du brug for en skræddersyet plan for' },
};

export function ContentGuideSection({ entityName, entityType }: ContentGuideSectionProps) {
  const { language } = useLanguage();
  const location = useLocation();

  const copy = GUIDE_COPY[language] || GUIDE_COPY.en;
  const slug = location.pathname.split('/').filter(Boolean).pop() || '';

  const bpoTr = getBPOPageTranslations(slug, language as SupportedLanguage);
  const digitalTr = getDigitalPageTranslations(slug, language);
  const detail = getBPODetailContent(slug, language as SupportedLanguage, {
    allowEnglishFallback: language === 'en',
  });
  const deepUi = getDeepDiveLabels(language);
  const base = getBPOFallbackCopy(language);

  const localizedDigitalName = [digitalTr.title, digitalTr.titleHighlight].filter(Boolean).join(' ').trim();
  const localizedName = entityType === 'service'
    ? bpoTr.serviceName || localizedDigitalName || entityName
    : entityName;

  const localizedDescription = bpoTr.description || digitalTr.description || base.description;
  const features = (detail.features || []).slice(0, 6);
  const kpis = (detail.kpis || []).slice(0, 6);
  const timeline = (detail.timeline || []).slice(0, 4);
  const faqs = (detail.faqs || []).slice(0, 4);

  return (
    <section className="py-16 md:py-20 bg-background" aria-label={`${copy.completeGuide}: ${localizedName}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="flex items-center gap-3 mb-8">
          <BookOpen className="w-6 h-6 text-primary" />
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            {copy.completeGuide}: {localizedName}
          </h2>
        </div>

        <div className="space-y-10">
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-2xl font-bold text-foreground mb-3">{copy.overview}</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">{localizedDescription}</p>
            {bpoTr.proofBullets && bpoTr.proofBullets.length > 0 && (
              <ul className="grid md:grid-cols-2 gap-2">
                {bpoTr.proofBullets.slice(0, 4).map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    {bullet}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-2xl font-bold text-foreground mb-3">{copy.capabilities}</h3>
            <p className="text-sm text-muted-foreground mb-4">{deepUi.whatsIncluded}</p>
            <div className="grid md:grid-cols-2 gap-4">
              {features.map((feature, idx) => (
                <div key={idx} className="rounded-lg border border-border bg-background p-4">
                  <p className="font-semibold text-foreground mb-1">{feature.title}</p>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-2xl font-bold text-foreground mb-4">{copy.outcomes}</h3>
            <div className="overflow-x-auto">
              <table className="w-full border border-border rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-muted/40">
                    <th className="text-left p-3 text-foreground font-semibold">{deepUi.metric}</th>
                    <th className="text-left p-3 text-foreground font-semibold">{deepUi.beforeInHouse}</th>
                    <th className="text-left p-3 text-primary font-semibold">{deepUi.afterOutsourced}</th>
                  </tr>
                </thead>
                <tbody>
                  {kpis.map((kpi, idx) => (
                    <tr key={idx} className="border-t border-border">
                      <td className="p-3 text-foreground">{kpi.metric}</td>
                      <td className="p-3 text-muted-foreground">{kpi.description || '—'}</td>
                      <td className="p-3 text-primary font-semibold">{kpi.target}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-2xl font-bold text-foreground mb-4">{copy.deployment}</h3>
            <div className="space-y-3">
              {timeline.map((step, idx) => (
                <div key={idx} className="rounded-lg border border-border bg-background p-4">
                  <p className="font-semibold text-primary mb-1">{step.day} — {step.phase}</p>
                  <p className="text-sm text-muted-foreground">{step.activities.join(' • ')}</p>
                </div>
              ))}
            </div>
          </div>

          {faqs.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="text-2xl font-bold text-foreground mb-4">{copy.faq}</h3>
              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <div key={idx} className="rounded-lg border border-border bg-background p-4">
                    <p className="font-semibold text-foreground mb-1">{faq.question}</p>
                    <p className="text-sm text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border bg-muted/20 p-6">
            <p className="text-foreground font-medium mb-4">
              {copy.tailoredPlan} {localizedName}?
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={`/${language}/contact`}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
              >
                {UI.bookACall(language)} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to={`/${language}/pricing`}
                className="inline-flex items-center gap-2 border border-border text-foreground font-semibold px-6 py-3 rounded-lg hover:bg-accent transition-colors"
              >
                {deepUi.pricingRoi}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
